<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import Swal from "sweetalert2";
import { mapActions } from "vuex";

export default {
    page: {
        title: "Wingband",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Wingband",
            header: {
                stag_number: "",
                breeders: "",
                farm_name: "",
                farm_address: "",
                province: "",
                chapter: "",
                contact_number: "",
            },
            wingband_data: [],
            wingband_date: "",
            selectedSeason: null,
            addRowModal: false,
            addAnotherRow: null,
            submitted: false,
            shouldShowDraftMessage: false,
        };
    },
    created() {
        const q = this.$route.query;
        const draft = localStorage.getItem("mobile_wingband_draft");

        if (q.total) {
            localStorage.removeItem("mobile_wingband_draft");
            this.header = {
                stag_number: q.stag_number || "",
                breeders: q.breeders || "",
                farm_name: q.farm_name || "",
                farm_address: q.farm_address || "",
                province: q.province || "",
                chapter: q.chapter || "",
                contact_number: q.contact_number || "",
            };
            this.wingband_data = Array.from({ length: parseInt(q.total, 10) }, () => ({
                wingband_number: "",
                feather_color: "",
                leg_color: "",
                comb_shape: "",
                nose_markings: "",
                feet_markings: "",
            }));
        } else if (draft) {
            const parsed = JSON.parse(draft);
            if (parsed.wingband_data && parsed.wingband_data.length > 0) {
                this.header = parsed.header || {};
                this.wingband_data = parsed.wingband_data;
                this.wingband_date = parsed.wingband_date || "";
                this.shouldShowDraftMessage = true;
            }
        } else {
            this.$router.push("/mobile-dashboard");
        }
    },
    computed: {
        duplicateWingbandNumbers() {
            const numbers = this.wingband_data.map(r => String(r.wingband_number).trim()).filter(n => n);
            return new Set(numbers.filter((num, idx) => numbers.indexOf(num) !== idx));
        },
    },
    methods: {
        ...mapActions("wingband", {
            createWingband: "createWingband",
        }),
        isFieldInvalid(row, field) {
            if (this.submitted && (!row[field] || String(row[field]).trim() === "")) {
                return true;
            }
            return false;
        },
        isDuplicateWingband(row) {
            const val = String(row.wingband_number).trim();
            return val !== "" && this.duplicateWingbandNumbers.has(val);
        },
        allowNumbersOnly(event) {
            const char = String.fromCharCode(event.which);
            if (!/[0-9]/.test(char)) {
                event.preventDefault();
            }
        },
        defaultRow() {
            return {
                wingband_number: "",
                feather_color: "",
                leg_color: "",
                comb_shape: "",
                nose_markings: "",
                feet_markings: "",
            };
        },
        addRow() {
            Swal.fire({
                title: "Warning",
                text: `Are you sure you want to add more rows?`,
                icon: "warning",
                showCancelButton: true,
            }).then((result) => {
                if (result.isConfirmed) {
                    for (let i = 0; i < this.addAnotherRow; i++) {
                        this.wingband_data.push(this.defaultRow());
                    }
                    this.addRowModal = false;
                }
            });
        },
        deleteRow(index) {
            Swal.fire({
                title: "Warning",
                text: `Are you sure you want to delete this row?`,
                icon: "warning",
                showCancelButton: true,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.wingband_data.splice(index, 1);
                }
            });
        },
        async submitData() {
            this.submitted = true;

            if (this.wingband_data.length === 0) {
                Swal.fire({
                    title: "Warning",
                    html: `No rows available. Please go <strong>back</strong> and start a new wingbanding.`,
                    icon: "warning",
                });
                return;
            }

            let isValid = true;
            for (const row of this.wingband_data) {
                const requiredFields = ["wingband_number", "feather_color", "leg_color", "comb_shape", "nose_markings", "feet_markings"];
                for (const field of requiredFields) {
                    if (!row[field] || String(row[field]).trim() === "") {
                        isValid = false;
                        break;
                    }
                }
                if (!isValid) break;
            }

            if (!isValid) {
                Swal.fire({ title: "Warning", text: "Please fill all the required fields.", icon: "warning" });
                return;
            }

            // Check for duplicate wingband numbers within this batch
            const wbNumbers = this.wingband_data.map(r => String(r.wingband_number).trim()).filter(n => n);
            const duplicates = [...new Set(wbNumbers.filter((num, idx) => wbNumbers.indexOf(num) !== idx))];
            if (duplicates.length > 0) {
                Swal.fire({
                    title: "Duplicate Wingband Numbers Detected",
                    html: `The following wingband numbers appear more than once in this batch:<br><br><strong>${duplicates.join(", ")}</strong><br><br>Please correct before saving.`,
                    icon: "warning",
                });
                return;
            }

            try {
                const confirmed = await Swal.fire({
                    title: "Are you sure?",
                    html: `You want to SAVE and EXIT?`,
                    icon: "warning",
                    confirmButtonColor: "#556ee6",
                    showCancelButton: true,
                    padding: "2em",
                }).then((result) => result.isConfirmed);

                if (!confirmed) return;

                if (!this.wingband_date) {
                    Swal.fire({ title: "Warning", text: "You forgot to fill the date.", icon: "warning" });
                    return;
                }

                if (!this.selectedSeason) {
                    Swal.fire({
                        title: "Warning",
                        text: "The selected date does not belong to any season. Please choose a valid date.",
                        icon: "warning",
                    });
                    return;
                }

                const fullWingbandData = this.wingband_data.map((row) => ({
                    stag_number: this.header.stag_number,
                    breeders: this.header.breeders,
                    farm_name: this.header.farm_name,
                    farm_address: this.header.farm_address,
                    province: this.header.province,
                    contact_number: this.header.contact_number,
                    wingband_number: row.wingband_number,
                    feather_color: row.feather_color,
                    leg_color: row.leg_color,
                    comb_shape: row.comb_shape,
                    nose_markings: row.nose_markings,
                    feet_markings: row.feet_markings,
                    wingband_date: this.wingband_date,
                    chapter: this.$route.query.chapter
                }));

                const pl = {
                    wingband_data: fullWingbandData,
                    wingband_date: this.wingband_date,
                    season: this.selectedSeason.label,
                };
                if(this.$route.query.chapter_id) {
                    pl.chapter_id = parseInt(this.$route.query.chapter_id)
                    pl.chapter = this.$route.query.chapter
                }

                const res = await this.createWingband(pl);
                if (res.status === 201) {
                    Swal.fire({
                        title: "Saved",
                        text: `Wingband for ${this.wingband_date} saved successfully!`,
                        icon: "success",
                    });
                    this.clearDraft();
                    this.goBack();
                } else {
                    Swal.fire({ title: "Failed", text: res.message, icon: "error" });
                }
            } catch (error) {
                console.error("Error creating wingband:", error);
                Swal.fire({ icon: "error", title: "Error!", text: "An unexpected error occurred. Please try again." });
            }
        },
        goBack() {
            this.$router.push({ path: "/mobile-dashboard" });
        },
        saveDraft() {
            localStorage.setItem("mobile_wingband_draft", JSON.stringify({
                header: this.header,
                wingband_data: this.wingband_data,
                wingband_date: this.wingband_date,
            }));
        },
        loadDraft() {
            if (this.shouldShowDraftMessage) {
                Swal.fire({
                    title: "Draft Restored",
                    text: "Your previous unsaved entries have been restored.",
                    icon: "info",
                    timer: 3000,
                    showConfirmButton: false,
                    timerProgressBar: true,
                });
                this.shouldShowDraftMessage = false;
            }
        },
        clearDraft() {
            localStorage.removeItem("mobile_wingband_draft");
        },
        getSeasonFromDate(date) {
            if (!date) return null;
            const d = new Date(date);
            const month = d.getMonth() + 1;
            const day = d.getDate();
            const mm = String(month).padStart(2, "0");
            const dd = String(day).padStart(2, "0");
            const md = mm + "-" + dd;

            if (md >= "01-02" && md <= "01-30") return { label: "EARLY BIRD", class: "badge-violet" };
            if (md >= "03-01" && md <= "03-30") return { label: "LOCAL", class: "badge-green" };
            if (md >= "04-01" && md <= "04-30") return { label: "NATIONAL", class: "badge-yellow" };
            if (md >= "06-01" && md <= "06-30") return { label: "LATEBORN", class: "badge-silver" };
            if (md >= "11-02" && md <= "11-20") return { label: "GENESIS", class: "badge-skyblue" };
            return null;
        },
    },
    watch: {
        wingband_data: {
            deep: true,
            handler() {
                this.saveDraft();
            },
        },
        wingband_date(val) {
            this.selectedSeason = this.getSeasonFromDate(val);
            this.saveDraft();
        },
    },
    mounted() {
        this.loadDraft();
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" />

        <!-- Header Info Strip -->
        <div class="card mb-3">
            <div class="card-body py-2 px-3">
                <div class="d-flex flex-wrap gap-3 align-items-center">
                    <span class="small"><strong>Name of Breeders:</strong> {{ header.breeders }}</span>
                    <span class="small"><strong>Farm Name:</strong> {{ header.farm_name }}</span>
                    <span class="small"><strong>Address:</strong> {{ header.farm_address }}</span>
                    <span class="small"><strong>Province:</strong> {{ header.province }}</span>
                    <span class="small"><strong>Chapter:</strong> {{ header.chapter }}</span>
                    <span class="small"><strong>Contact No.:</strong> {{ header.contact_number }}</span>
                    <span class="small"><strong>Stag Registry No.:</strong> {{ header.stag_number }}</span>
                </div>
            </div>
        </div>

        <!-- Controls Row -->
        <div class="card mb-3">
            <div class="card-body py-2">
                <div class="d-flex flex-wrap align-items-center gap-2">
                    <button class="btn btn-info" @click="addRowModal = true">
                        <i class="bx bx-plus"></i> ADD MORE ROWS
                    </button>
                    <div class="d-flex align-items-center gap-2 flex-wrap ms-auto">
                        <label class="fw-semibold mb-0">Date:</label>
                        <input type="date" class="form-control" style="width: 180px;" v-model="wingband_date">
                        <span v-if="selectedSeason"
                            :class="['badge', 'fs-6', 'px-3', 'py-2', 'text-uppercase', 'season-badge', selectedSeason.class]">
                            {{ selectedSeason.label }}
                        </span>
                        <span v-else class="badge fs-6 px-3 py-2 text-uppercase season-badge badge-secondary">
                            NO SEASON FOUND
                        </span>
                    </div>
                    <button class="btn btn-danger" @click="goBack()">Back</button>
                </div>
            </div>
        </div>

        <!-- Wingband Table -->
        <div class="card p-0 mb-3">
            <div class="card-body p-0">
                <div class="table-responsive">
                    <table class="table table-bordered mb-0">
                        <thead>
                            <tr class="bg-light">
                                <th class="text-center" style="width: 40px;"></th>
                                <th class="text-center" style="width: 40px;">ID</th>
                                <th>WINGBAND NO.</th>
                                <th>FEATHER COLOR</th>
                                <th>LEG COLOR</th>
                                <th>COMB SHAPE</th>
                                <th>NOSE MARKINGS</th>
                                <th>FEET MARKINGS</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in wingband_data" :key="index">
                                <td class="text-center align-middle">
                                    <button class="btn btn-light btn-sm p-0" @click="deleteRow(index)">
                                        <i class="mdi mdi-trash-can fs-4 text-danger"></i>
                                    </button>
                                </td>
                                <td class="text-center align-middle">{{ index + 1 }}</td>
                                <td>
                                    <input class="form-control" type="text"
                                        v-model="row.wingband_number"
                                        @keypress="allowNumbersOnly"
                                        autocomplete="off"
                                        :class="{
                                            'is-invalid': isFieldInvalid(row, 'wingband_number') || isDuplicateWingband(row),
                                            'border-warning': isDuplicateWingband(row),
                                        }">
                                    <div v-if="isDuplicateWingband(row)" class="text-warning small mt-1">
                                        Duplicate
                                    </div>
                                </td>
                                <td>
                                    <select class="form-control" v-model="row.feather_color"
                                        :class="{ 'is-invalid': isFieldInvalid(row, 'feather_color') }">
                                        <option value="">Select</option>
                                        <option value="N/A">N/A</option>
                                        <option value="GRY-GREY">GRY-GREY</option>
                                        <option value="BLK-BLACK">BLK-BLACK</option>
                                        <option value="W-WHITE">W-WHITE</option>
                                        <option value="PY-PYLE">PY-PYLE</option>
                                        <option value="PU-PUMPKIN">PU-PUMPKIN</option>
                                        <option value="GLD-GOLD">GLD-GOLD</option>
                                        <option value="R-RED">R-RED</option>
                                        <option value="D-DOM">D-DOM</option>
                                        <option value="BL-BLUE">BL-BLUE</option>
                                        <option value="DW-DIRTY WHITE">DW-DIRTY WHITE</option>
                                        <option value="BR-BROWN RED">BR-BROWN RED</option>
                                        <option value="SPGL-SPANGLE">SPGL-SPANGLE</option>
                                        <option value="WG-WHITE GOLD">WG-WHITE GOLD</option>
                                    </select>
                                </td>
                                <td>
                                    <select class="form-control" v-model="row.leg_color"
                                        :class="{ 'is-invalid': isFieldInvalid(row, 'leg_color') }">
                                        <option value="">Select</option>
                                        <option value="N/A">N/A</option>
                                        <option value="W-WHITE">W-WHITE</option>
                                        <option value="BL-BLUE">BL-BLUE</option>
                                        <option value="Y-YELLOW">Y-YELLOW</option>
                                        <option value="BLK-BLACK">BLK-BLACK</option>
                                        <option value="G-GREEN">G-GREEN</option>
                                        <option value="S-SOTA">S-SOTA</option>
                                    </select>
                                </td>
                                <td>
                                    <select class="form-control" v-model="row.comb_shape"
                                        :class="{ 'is-invalid': isFieldInvalid(row, 'comb_shape') }">
                                        <option value="">Select</option>
                                        <option value="N/A">N/A</option>
                                        <option value="S-STRAIGHT COMB">S-STRAIGHT COMB</option>
                                        <option value="P-PEA COMB">P-PEA COMB</option>
                                    </select>
                                </td>
                                <td>
                                    <select class="form-control" v-model="row.nose_markings"
                                        :class="{ 'is-invalid': isFieldInvalid(row, 'nose_markings') }">
                                        <option value="">Select</option>
                                        <option value="N/A">N/A</option>
                                        <option value="L-LEFT NOSE">L-LEFT NOSE</option>
                                        <option value="R-RIGHT NOSE">R-RIGHT NOSE</option>
                                        <option value="D-DOUBLE NOSE">D-DOUBLE NOSE</option>
                                    </select>
                                </td>
                                <td>
                                    <select class="form-control" v-model="row.feet_markings"
                                        :class="{ 'is-invalid': isFieldInvalid(row, 'feet_markings') }">
                                        <option value="">Select</option>
                                        <option value="N/A">N/A</option>
                                        <option value="RODL-RIGHT OUT DOUBLE LEFT">RODL-RIGHT OUT DOUBLE LEFT</option>
                                        <option value="RIDL-RIGHT IN DOUBLE LEFT">RIDL-RIGHT IN DOUBLE LEFT</option>
                                        <option value="RILO-RIGHT IN LEFT OUT">RILO-RIGHT IN LEFT OUT</option>
                                        <option value="DRLO-DOUBLE RIGHT LEFT OUT">DRLO-DOUBLE RIGHT LEFT OUT</option>
                                        <option value="DR-DOUBLE RIGHT">DR-DOUBLE RIGHT</option>
                                        <option value="RI-RIGHT IN">RI-RIGHT IN</option>
                                        <option value="RO-RIGHT OUT">RO-RIGHT OUT</option>
                                        <option value="DL-DOUBLE LEFT">DL-DOUBLE LEFT</option>
                                        <option value="LO-LEFT OUT">LO-LEFT OUT</option>
                                        <option value="LI-LEFT IN">LI-LEFT IN</option>
                                        <option value="II-RIGHT OUT SLIT LEFT OUT SLIT">II-RIGHT OUT SLIT LEFT OUT SLIT</option>
                                        <option value="4W-FOUR WEBBER">4W-FOUR WEBBER</option>
                                        <option value="OO-RIGHT OUT LEFT OUT PUNCH">OO-RIGHT OUT LEFT OUT PUNCH</option>
                                    </select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <button class="btn btn-success fs-2 fw-bold w-100 py-3" @click="submitData()">SAVE & EXIT</button>

        <!-- Add Rows Modal -->
        <b-modal v-model="addRowModal" size="sm" title="Add Rows" title-class="font-18" hide-footer>
            <label>Number of rows</label>
            <input class="form-control mb-3 w-100" type="number" v-model="addAnotherRow" />
            <div class="text-end">
                <button class="btn btn-success" @click="addRow()">Add</button>
            </div>
        </b-modal>
    </Layout>
</template>

<style scoped>
th {
    text-transform: uppercase;
    white-space: nowrap;
}
td {
    vertical-align: middle;
}
.table > :not(caption) > * > * {
    padding: 0.4rem 0.3rem;
}

.badge-violet   { background-color: #6f42c1; color: #fff; font-weight: 700; border-radius: 6px; }
.badge-green    { background-color: #34c38f; color: #fff; font-weight: 700; border-radius: 6px; }
.badge-yellow   { background-color: #f1b44c; color: #fff; font-weight: 700; border-radius: 6px; }
.badge-silver   { background-color: #adb5bd; color: #fff; font-weight: 700; border-radius: 6px; }
.badge-skyblue  { background-color: #0dcaf0; color: #fff; font-weight: 700; border-radius: 6px; }
.badge-secondary { background-color: #74788d; color: #fff; font-weight: 700; border-radius: 6px; }

.season-badge {
    font-size: 0.85rem !important;
    padding: 0.45rem 1rem !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    white-space: nowrap;
}
</style>
